import type { IconType } from 'react-icons';
import { IoMusicalNote, IoPerson, IoPulse, IoSparkles } from 'react-icons/io5';
import { NodeTypeId } from '../types/graphNodes';
import { FilterOperator } from '../filters/types';
import { serializeFilters } from '../filters/serialization';
import { deityIconUrl } from '../bhajan/deityIcon';
import { APP } from '../app/appConfig';
import type { AppConfig } from '../app/types';
import type { BrowseAccent } from './BrowseIcon';

export interface BrowseFacet {
  key: 'deity' | 'ragam' | 'talam' | 'composer';
  nodeTypeId: NodeTypeId;
  edgeName: string;
  label: string;        // singular, e.g. 'Deity'
  pluralLabel: string;  // e.g. 'Deities'
  /** When set, the value list renders as image cards instead of text rows. */
  image?: (valueNodeId: number) => string;
  /**
   * The /browse tile's glyph and tint. Required, not optional: a new facet with
   * no icon should fail to compile rather than draw a tile that silently sits
   * out of line with its neighbours.
   */
  icon: IconType;
  accent: BrowseAccent;
}

/*
 * Icons and accents mirror the native app's browse root
 * (jnana-native/src/bmScreens/browse/root/index.tsx) glyph for glyph, so the
 * same concept wears the same face on both surfaces. Native's palette names map
 * onto web's theme.css tokens: violet -> purple, blue -> indigo, teal -> emerald.
 *
 * Talam is the one entry with nothing to copy — the native browse root has no
 * Talams row at all — so it gets `pulse`, the beat-cycle reading of a talam, in
 * emerald, the accent no other tile on the page is using.
 */
export const ALL_FACETS: Record<BrowseFacet['key'], BrowseFacet> = {
  deity: { key: 'deity', nodeTypeId: NodeTypeId.DEITY, edgeName: 'deity', label: 'Deity', pluralLabel: 'Deities', image: deityIconUrl, icon: IoSparkles, accent: 'gold' },
  ragam: { key: 'ragam', nodeTypeId: NodeTypeId.RAGAM, edgeName: 'ragam', label: 'Ragam', pluralLabel: 'Ragams', icon: IoMusicalNote, accent: 'purple' },
  talam: { key: 'talam', nodeTypeId: NodeTypeId.TALAM, edgeName: 'talam', label: 'Talam', pluralLabel: 'Talams', icon: IoPulse, accent: 'emerald' },
  composer: { key: 'composer', nodeTypeId: NodeTypeId.COMPOSER, edgeName: 'composer', label: 'Composer', pluralLabel: 'Composers', icon: IoPerson, accent: 'rose' },
};

/** The facet hubs an app has, in the order its config declares them. */
export const browseFacetsFor = (cfg: AppConfig): BrowseFacet[] =>
  cfg.browseFacetKeys.map((k) => ALL_FACETS[k]);

export const BROWSE_FACETS: BrowseFacet[] = browseFacetsFor(APP);

export const getFacet = (key: string | undefined): BrowseFacet | undefined =>
  BROWSE_FACETS.find((f) => f.key === key);

/**
 * Where a "browse" call-to-action should point for a given app.
 *
 * /browse and /browse/:facet only mount when the app declares at least one
 * facet hub (App.tsx), so a zero-facet tenant has no /browse page at all and
 * any CTA aimed there would be a dead link. This is the ONE place that answers
 * the question — the nav (appPolicy.navFor), the home page CTA and the hero
 * CTA all read it, so they cannot drift apart or outlive a config change.
 */
export const browseHrefFor = (cfg: AppConfig): string =>
  cfg.browseFacetKeys.length > 0 ? '/browse' : '/bhajans';

/** browseHrefFor, resolved for the app that was actually built. */
export const BROWSE_HREF: string = browseHrefFor(APP);

/** The bhajan edge that carries a Western Notes score (nodeTypes.ts). */
export const WESTERN_NOTES_EDGE = 'westernNotes';

/** Build the /bhajans URL pre-filtered to a single facet value (edge HasSpecific). */
export function facetFilterHref(facet: BrowseFacet, valueNodeId: number): string {
  const serialized = serializeFilters([
    {
      id: 'browse',
      filterId: 'edge',
      operator: FilterOperator.HasSpecific,
      value: { edgeName: facet.edgeName, targetNodeIds: [valueNodeId] },
    },
  ]);
  // encodeURIComponent so base64 '+' '/' '=' survive the query string round-trip.
  return `/bhajans?filters=${encodeURIComponent(serialized)}`;
}

/**
 * Build the /bhajans URL pre-filtered to "carries this edge at all", whatever
 * the edge points at — e.g. every bhajan that has a Western Notes score.
 *
 * Operator is `Has` and must stay `Has`. FilterOperator.HasAny and HasNone are
 * declared in the enum but have no case in edgeFilter.execute's switch, so they
 * fall through to `default: return true` and match every node silently
 * (edgeFilter.ts:376-377).
 */
export function edgePresenceFilterHref(edgeName: string): string {
  const serialized = serializeFilters([
    { id: 'browse', filterId: 'edge', operator: FilterOperator.Has, value: { edgeName } },
  ]);
  return `/bhajans?filters=${encodeURIComponent(serialized)}`;
}

/**
 * Build the /bhajans URL pre-filtered to a boolean flag the slim catalog index
 * stamps on the node itself — e.g. `hasSvaras`, which cannot be derived on the
 * client because the index drops lyrics nodes entirely.
 */
export function nodeFlagFilterHref(flag: string): string {
  const serialized = serializeFilters([
    { id: 'browse', filterId: 'flag', operator: FilterOperator.Is, value: flag },
  ]);
  return `/bhajans?filters=${encodeURIComponent(serialized)}`;
}
